import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import moment from "moment-timezone";

import Datepicker from "../../../Components/Datepicker";

import "./DatepickerStateful.scss";

/*
Constants and helpers
*/

// Date format should be one of two strings: 'local' or 'utc',
// but can be any string - anything but 'utc' is equivalent to 'local'
const DEFAULT_TIME_FORMAT = "local";

const hasNotChanged = ({
  timeEnd,
  timeStart,
  currentTimeEnd,
  currentTimeStart,
}) => {
  const notChanged =
    moment(timeEnd).valueOf() === moment(currentTimeEnd).valueOf() &&
    moment(timeStart).valueOf() === moment(currentTimeStart).valueOf();

  return notChanged;
};

/*
Component

Properties:

  defaultTimeFormat - should be one of two strings, 'local' or 'utc';
                      but can be any string -
                      anything but 'utc' is equivalent to 'local'...

  timeEnd, timeStart - initial dates, moment or string

  onClose - function called when the Datepicker closes
                  (irrespective of weather the range changed or not)
                  with two argument:
                    - 'range', object of the form
                      {rangeEnd: Moment, rangeStart: Moment}
                    - 'changed', Boolean, true if the date range has
                      changed, false if it has not
*/

const DatepickerStateful = (props) => {
  const { defaultTimeFormat, onClose, timeEnd, timeStart } = props;

  const [currentTimeEnd, setCurrentTimeEnd] = useState("");
  const [currentTimeStart, setCurrentTimeStart] = useState("");

  const [timeFormat, setTimeFormat] = useState(DEFAULT_TIME_FORMAT);

  useEffect(() => {
    setCurrentTimeEnd(moment(timeEnd));
  }, [timeEnd]);

  useEffect(() => {
    setCurrentTimeStart(moment(timeStart));
  }, [timeStart]);

  useEffect(() => {
    setTimeFormat(defaultTimeFormat);
  }, [defaultTimeFormat]);

  // 'onChange' function required by the Datepicker
  const pickerOnChange = (key, value) => {
    switch (key) {
      case "rangeEnd": {
        setCurrentTimeEnd(value);

        return;
      }

      case "rangeStart": {
        setCurrentTimeStart(value);

        return;
      }

      case "timeFormat": {
        setTimeFormat(value);

        break;
      }

      default:
    }
  };

  const pickerOnClose = () => {
    const changed = !hasNotChanged({
      timeEnd,
      timeStart,
      currentTimeEnd,
      currentTimeStart,
    });

    onClose({
      range: {
        rangeEnd: currentTimeEnd,
        rangeStart: currentTimeStart,
      },
      changed,
    });
  };

  return (
    <div className="datepicker-stateful">
      <Datepicker
        onChange={pickerOnChange}
        onClose={pickerOnClose}
        rangeEnd={currentTimeEnd}
        rangeStart={currentTimeStart}
        timeFormat={timeFormat}
      />
    </div>
  );
};

DatepickerStateful.propTypes = {
  defaultTimeFormat: PropTypes.string,
  onClose: PropTypes.func,
  timeEnd: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
  timeStart: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
};
DatepickerStateful.defaultProps = {
  defaultTimeFormat: DEFAULT_TIME_FORMAT,
  onClose: () => {},
  timeEnd: "",
  timeStart: "",
};

export default DatepickerStateful;
